/** Tiempo visible del alert antes de iniciar el cierre automático. */
export const VAULT_ALERT_AUTO_DISMISS_MS = 8000;

/** Duración de la animación de salida (debe coincidir con `slideOutRight`). */
export const VAULT_ALERT_CLOSE_ANIMATION_MS = 200;

export interface VaultAlertAutoDismissHandlers {
  /** Marca el alert como "cerrándose" para disparar la animación de salida. */
  onBeginClose: () => void;
  /** Quita el alert una vez terminó la animación. */
  onDismiss: () => void;
}

/**
 * Orquesta el auto-cierre de un alert en dos fases (animación → quitar), sin acoplarse
 * a Angular. Usa `setTimeout`/`clearTimeout` globales, así que es testeable con fake timers.
 */
export class VaultAlertAutoDismissController {
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;
  private animationTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly handlers: VaultAlertAutoDismissHandlers,
    private readonly autoDismissMs: number = VAULT_ALERT_AUTO_DISMISS_MS,
    private readonly closeAnimationMs: number = VAULT_ALERT_CLOSE_ANIMATION_MS,
  ) {}

  /** Programa el auto-cierre; reinicia cualquier temporizador previo. Llamar al mostrar el alert. */
  start(): void {
    this.cancel();
    this.dismissTimer = setTimeout(() => {
      this.dismissTimer = null;
      this.handlers.onBeginClose();
      this.animationTimer = setTimeout(() => {
        this.animationTimer = null;
        this.handlers.onDismiss();
      }, this.closeAnimationMs);
    }, this.autoDismissMs);
  }

  /** Cancela temporizadores pendientes (cierre manual, nuevo alert o destrucción del componente). */
  cancel(): void {
    if (this.dismissTimer !== null) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
    if (this.animationTimer !== null) {
      clearTimeout(this.animationTimer);
      this.animationTimer = null;
    }
  }

  /** `true` si hay un auto-cierre programado o en curso. */
  isActive(): boolean {
    return this.dismissTimer !== null || this.animationTimer !== null;
  }
}
